import AbstractFormField, {AbstractFormFieldProperties} from "./AbstractFormField";
import {CSSProperties, ReactNode} from "react";
import {BorderStyle, Field} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalAnnotation} from "../../../../shared/models/PortalAnnotation";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {InputTextarea, InputTextareaProps} from "primereact/inputtextarea";
import DomHelper from "../../../../shared/helper/DomHelper";

interface TextFieldProperties extends AbstractFormFieldProperties, InputTextareaProps {
	onValue: (value: string) => void;
}

interface TextFieldStatus {
	hasFocus: boolean;
}

export class TextField extends AbstractFormField<TextFieldProperties, TextFieldStatus> {
	constructor(props: Readonly<TextFieldProperties>) {
		super(props);

		this.state = {
			hasFocus: false
		};

		this.handleKeydown = this.handleKeydown.bind(this);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "enter" && this.state.hasFocus) {
			event.stopPropagation();
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('keydown', this.handleKeydown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeydown);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let field: Field = Field.fromJson(this.props.field.get());

		if (field.isRichText) {
			// Skip richtext fields
			return null;
		}

		let fieldElements: Array<ReactNode> = [];
		for (let annotation of FunctionHelper.applyValue(field.annotation, Field.getAnnotationDefault())) {
			if (!this.shouldRenderAnnotation(annotation)) {
				continue;
			}

			let portalAnnotation: PortalAnnotation = PortalAnnotation.fromJson(
				FunctionHelper.applyValue(annotation, Field.getAnnotationDefault())
			);

			let fieldStyle: CSSProperties = {
				...this.computeFieldsStyle(portalAnnotation),
				...this.props.style
			};

			if (FunctionHelper.exists(portalAnnotation.border)) {
				fieldStyle.borderColor = portalAnnotation.border!.interiorColor;
				fieldStyle.borderWidth = portalAnnotation.border!.width!.toString();

				if (portalAnnotation.border!.style === BorderStyle.Dashed) {
					fieldStyle.borderStyle = "dashed";
				}
			}

			fieldElements.push(
				<InputTextarea className={"field " + field.subType!.toLowerCase() + "-field"} style={fieldStyle}
							   value={DomHelper.decodeHtmlEntities(this.props.value + "")}
							   key={field.objectKey + " " + portalAnnotation.objectKey} disabled={this.props.disabled}
							   onFocus={() => this.setState({hasFocus: true})}
							   onBlur={() => this.setState({hasFocus: false})}
							   tabIndex={this.getTabIndex(annotation.objectKey)}
							   onChange={(event) => {
								   this.props.onValue(event.target.value);
							   }}/>
			);
		}

		return fieldElements;
	}
}